import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, ShoppingBag, Newspaper, Briefcase, MessageSquare } from "lucide-react";
import HeroSlider from "@/components/HeroSlider";

export default function PublicHome() {
  const [products, setProducts] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [careers, setCareers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: p }, { data: u }, { data: c }] = await Promise.all([
        api.get("/public/products?tenant_slug=abrah"),
        api.get("/public/updates?tenant_slug=abrah"),
        api.get("/public/careers?tenant_slug=abrah"),
      ]);
      if (!cancelled) { setProducts(p.slice(0, 6)); setUpdates(u.slice(0, 3)); setCareers(c.slice(0, 3)); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      {/* Admin-managed rotating banners (renders nothing if none exist) */}
      <HeroSlider tenantSlug="abrah" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-950 via-emerald-900 to-amber-900 text-white">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,rgba(245,158,11,0.4),transparent_40%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-4">Welcome to Abrah Supermarket</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">
            Everyday essentials,<br />delivered with care across Uganda.
          </h1>
          <p className="mt-6 text-emerald-100/90 text-base sm:text-lg max-w-2xl">
            Shop from six branches, get news and promotions, or join our growing team — all in one place.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/site/shop"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-amber-500 text-emerald-950 font-semibold hover:bg-amber-400">
              Shop now <ArrowRight size={16} />
            </Link>
            <Link to="/site/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-emerald-200/30 hover:bg-emerald-900">
              Share feedback
            </Link>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { to: "/site/shop", icon: ShoppingBag, label: "Shop", desc: "Browse products" },
          { to: "/site/news", icon: Newspaper, label: "News", desc: "Latest updates" },
          { to: "/site/careers", icon: Briefcase, label: "Careers", desc: "Join us" },
          { to: "/site/contact", icon: MessageSquare, label: "Feedback", desc: "Tell us anything" },
        ].map((q) => {
          const Icon = q.icon;
          return (
            <Link key={q.to} to={q.to} className="group bg-white border border-gray-200 rounded-md p-5 hover:border-emerald-900 transition-colors">
              <div className="h-10 w-10 rounded-md bg-emerald-50 text-emerald-900 border border-emerald-200 flex items-center justify-center mb-3 group-hover:bg-emerald-900 group-hover:text-amber-300">
                <Icon size={18} />
              </div>
              <div className="font-display font-semibold text-gray-900">{q.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{q.desc}</div>
            </Link>
          );
        })}
      </section>

      {/* Featured products */}
      {products.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-gray-900">Featured products</h2>
            <Link to="/site/shop" className="text-sm text-emerald-900 hover:underline">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {products.map((p) => (
              <ProductCard key={p.id} p={p} compact />
            ))}
          </div>
        </section>
      )}

      {updates.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl font-bold text-gray-900">Latest news</h2>
            <Link to="/site/news" className="text-sm text-emerald-900 hover:underline">All updates →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {updates.map((u) => (
              <article key={u.id} className="bg-white border border-gray-200 rounded-md p-4">
                <div className="text-[10px] uppercase tracking-widest text-amber-700 mb-1">{u.category || "news"}</div>
                <div className="font-display font-semibold text-gray-900 line-clamp-2">{u.title}</div>
                <p className="text-sm text-gray-600 mt-1 line-clamp-3">{u.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {careers.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 pb-16">
          <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">Open positions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {careers.map((c) => (
              <Link key={c.id} to={`/site/careers/${c.id}`}
                className="bg-white border border-gray-200 rounded-md p-4 hover:border-emerald-900">
                <div className="font-display font-semibold text-gray-900">{c.title}</div>
                <div className="text-xs text-gray-500 mt-1">{c.location || "Uganda"} · {(c.employment_type || "").replaceAll("_", " ")}</div>
                <div className="mt-3 text-xs text-emerald-900 font-semibold">View role →</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export function ProductCard({ p, compact, onAdd }) {
  const { publicProductImageUrl } = require("@/lib/api");
  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
      <div className="aspect-square bg-amber-50/40 flex items-center justify-center">
        {p.image_file_id ? (
          <img src={publicProductImageUrl(p.image_file_id)} alt={p.name} className="object-cover w-full h-full" />
        ) : (
          <ShoppingBag size={28} className="text-amber-700/40" />
        )}
      </div>
      <div className="p-3">
        <div className="font-medium text-gray-900 text-sm line-clamp-2">{p.name}</div>
        {!compact && p.description && <div className="text-xs text-gray-500 line-clamp-2 mt-1">{p.description}</div>}
        <div className="flex items-center justify-between mt-2">
          <div className="font-display font-bold text-emerald-900 text-sm">
            {p.currency || "UGX"} {Number(p.price || 0).toLocaleString()}
          </div>
          {onAdd && (
            <button onClick={() => onAdd(p)}
              className="text-xs px-2 py-1 rounded-md bg-emerald-900 text-white hover:bg-emerald-950"
              data-testid={`shop-add-${p.id}`}>
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
